import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { getTimeDifference } from "shared/lib/date/getTimeDifference"

import { useForcedUpdate } from "./useForcedUpdate"

const MINUTE_IN_MILLISECONDS = 60000

export function useTimeDifferenceText(
  timeStamp: string | null | undefined,
  options?: { currentTime?: string }
) {
  const { t } = useTranslation()
  const [value, forceUpdate] = useForcedUpdate()
  const timeDifference = useMemo(
    () =>
      timeStamp
        ? getTimeDifference(
            new Date(timeStamp),
            options?.currentTime ? new Date(options?.currentTime) : new Date()
          )
        : null,
    [timeStamp, options, value]
  )

  useEffect(() => {
    const interval = setInterval(() => forceUpdate(), MINUTE_IN_MILLISECONDS)
    return () => clearInterval(interval)
  }, [])

  const getTimeDifferenceText = useCallback(
    (
      difference: number,
      unitTranslations: { Singular: string; Plural: string }
    ) =>
      t(`Time.Ago.${difference === 1 ? "Singular" : "Plural"}`, {
        duration: difference,
        unit:
          difference === 1 ? unitTranslations.Singular : unitTranslations.Plural
      }),
    [t]
  )

  return useMemo(() => {
    if (!timeDifference) {
      return null
    }

    if (timeDifference.yearsDifference) {
      return getTimeDifferenceText(
        timeDifference.yearsDifference,
        t("Time.Ago.Year", { returnObjects: true })
      )
    }
    if (timeDifference.monthsDifference) {
      return getTimeDifferenceText(
        timeDifference.monthsDifference,
        t("Time.Ago.Month", { returnObjects: true })
      )
    }
    if (timeDifference.daysDifference) {
      return getTimeDifferenceText(
        timeDifference.daysDifference,
        t("Time.Ago.Day", { returnObjects: true })
      )
    }
    if (timeDifference.hoursDifference) {
      return getTimeDifferenceText(
        timeDifference.hoursDifference,
        t("Time.Ago.Hour", { returnObjects: true })
      )
    }
    if (timeDifference.minutesDifference) {
      return getTimeDifferenceText(
        timeDifference.minutesDifference,
        t("Time.Ago.Minute", { returnObjects: true })
      )
    }
    return getTimeDifferenceText(
      1,
      t("Time.Ago.Minute", { returnObjects: true })
    )
  }, [timeDifference])
}
